import { FunctionComponent, useEffect, useState } from 'react';
import {
    Card,
    CardActions,
    CardContent,
    Checkbox,
    FormControlLabel,
    Grow,
    styled,
    Typography,
} from '@mui/material';
import { FlexContainer } from '@/components/Layout/Container';
import { BookingOptions } from '@/components/BookingOptions/BookingOptions';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import IconButton from '@mui/material/IconButton';
import {
    BulkUpdateActionPayload,
    BulkUpdateType,
    ConfigurationCollection,
    ConfigurationState,
} from '@/models/bulkUpdate';
import { initialBookingOptions } from './ConfigurationsPanelContainer';
import { Select } from '@/components/Select/Select';
import { SIDE_NAV_WIDTH } from '@/layouts/sidebar/SidebarLayout';
import { DateRangePicker } from '@/components/DatePicker/DateRangePicker';
import { DateRange } from '@/models/general';
import { LoadingButton } from '@mui/lab';
import { useConfigurationPanelContext } from '@/pages/productConfiguration/components/ProductConfigurationBulkUpdate/ConfigurationPanelContext';
import { ConditionLinkAutocomplete } from '@/components/ConditionLinkAutocomplete/ConditionLinkAutocomplete';

const PanelRow = styled(FlexContainer)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    alignItems: 'center',
}));

type ConfigurationsPanelProps = {
    numOfConfigurations: number;
    isActive: boolean;
    removeConditions: boolean;
    selectedConditionLinkIds: string[];
    configurationCollection: ConfigurationCollection;
    validityRange: DateRange;
    startDateValidityRange: DateRange;
    onConfigurationsChange: (action: BulkUpdateActionPayload) => void;
    configurationState: ConfigurationState;
    onConfigurationStateChange: (nextState: ConfigurationState) => void;
    onSave: () => void;
    isLoading: boolean;
};

export const ConfigurationsPanel: FunctionComponent<
    ConfigurationsPanelProps
> = ({
    numOfConfigurations,
    isActive,
    onConfigurationsChange,
    configurationCollection,
    configurationState,
    onConfigurationStateChange,
    validityRange,
    startDateValidityRange,
    onSave,
    isLoading,
    selectedConditionLinkIds,
}) => {
    const {
        activeStatus,
        setBookingOptions,
        setRemoveConditions,
        setValidityOptions,
        setStartDateValidityOptions,
        setRemoveStartDateValidFromTo,
        setRemoveValidFromTo,
        setDynamicPricing,
    } = configurationState;
    const [shrink, setShrink] = useState(false);
    const { setIsCollapsed } = useConfigurationPanelContext();

    useEffect(() => {
        if (numOfConfigurations === 0) {
            setShrink(false);
        }
    }, [numOfConfigurations]);

    useEffect(() => {
        setIsCollapsed(shrink);
    }, [shrink]);

    useEffect(() => {
        if (numOfConfigurations === 0) {
            setShrink(false);
        }
    }, [numOfConfigurations]);

    const onChangeCheckbox = (key: keyof ConfigurationState) => (e) => {
        const { checked } = e.target;

        const nextState = {
            ...configurationState,
            [key]: checked,
        };

        if (key === 'setDynamicPricing') {
            nextState.setRemoveConditions = false;
        } else if (key === 'setRemoveConditions') {
            nextState.setDynamicPricing = false;
        }

        onConfigurationStateChange({
            ...nextState,
        });

        switch (key) {
            case 'activeStatus':
                onConfigurationsChange({
                    type: BulkUpdateType.ACTIVE_STATUS,
                    isActive: true,
                });
                break;
            case 'setDynamicPricing':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_REMOVE_CONDITION,
                    payload: false,
                });
                break;
            case 'setRemoveConditions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_DYNAMIC_PRICING,
                    conditionLinkIds: [],
                });
                onConfigurationsChange({
                    type: BulkUpdateType.SET_REMOVE_CONDITION,
                    payload: true,
                });
                break;
            case 'setBookingOptions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_BOOKING_OPTIONS,
                    configurations: checked ? initialBookingOptions : null,
                });
                break;
            case 'setValidityOptions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_VALIDITY_OPTIONS,
                    payload: checked ? [null, null] : null,
                });
                break;
            case 'setStartDateValidityOptions':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_START_DATE_VALIDITY_OPTIONS,
                    payload: checked ? [null, null] : null,
                });
                break;
            case 'setRemoveValidFromTo':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_VALIDITY_OPTIONS,
                    payload: null,
                });
                break;
            case 'setRemoveStartDateValidFromTo':
                onConfigurationsChange({
                    type: BulkUpdateType.SET_START_DATE_VALIDITY_OPTIONS,
                    payload: null,
                });
                break;
        }
    };

    const containerHeight = setBookingOptions ? 600 : 400;
    return (
        <Grow in={numOfConfigurations > 0}>
            <div
                style={{
                    position: 'fixed',
                    height: containerHeight,
                    left: SIDE_NAV_WIDTH,
                    top: `calc(100vh - ${containerHeight}px)`,
                    bottom: 0,
                    right: 0,
                    zIndex: 10,
                    ...(shrink && {
                        transform: `translateY(${
                            setBookingOptions ? 510 : 320
                        }px)`,
                    }),
                }}
            >
                <Card
                    elevation={3}
                    sx={{
                        height: '100%',
                        width: '100%',
                        overflowY: 'scroll',
                        position: 'relative',
                        pb: 10,
                    }}
                >
                    <CardContent
                        sx={{
                            display: 'flex',
                            height: 60,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            alignItems: 'center',
                        }}
                    >
                        <Typography>
                            {numOfConfigurations}{' '}
                            {numOfConfigurations === 1
                                ? 'Konfiguration'
                                : 'Konfigurationen'}{' '}
                            gewählt
                        </Typography>

                        <IconButton
                            sx={(theme) => ({
                                backgroundColor: theme.palette.primary.main,
                                color: '#fff',
                                ml: 'auto',
                                transform: shrink
                                    ? 'rotateZ(180deg)'
                                    : 'rotateZ(0deg)',
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.dark,
                                },
                            })}
                            onClick={() => {
                                setShrink((prevState) => !prevState);
                            }}
                        >
                            <KeyboardArrowUpIcon />
                        </IconButton>
                    </CardContent>
                    <CardContent
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        <PanelRow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={activeStatus}
                                        onChange={onChangeCheckbox(
                                            'activeStatus'
                                        )}
                                    />
                                }
                                label='Markierte Produkte für Verkauf aktivieren / deaktivieren (alle Buchungskanäle)'
                            />

                            {activeStatus && (
                                <Select
                                    label='Status'
                                    sx={{
                                        width: '300px',
                                    }}
                                    value={
                                        isActive === null
                                            ? 'active'
                                            : isActive
                                              ? 'active'
                                              : 'inActive'
                                    }
                                    onChange={(e) => {
                                        const { value } = e.target;
                                        onConfigurationsChange({
                                            type: BulkUpdateType.ACTIVE_STATUS,
                                            isActive: value === 'active',
                                        });
                                    }}
                                >
                                    <option value='active'>Aktiv</option>
                                    <option value='inActive'>Inaktiv</option>
                                </Select>
                            )}
                        </PanelRow>

                        <PanelRow
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setDynamicPricing}
                                        onChange={onChangeCheckbox(
                                            'setDynamicPricing'
                                        )}
                                    />
                                }
                                label='Regelwerk für markierte Produkte auswählen'
                            />

                            {setDynamicPricing && (
                                <FlexContainer
                                    sx={{
                                        minWidth: '400px',
                                    }}
                                >
                                    <ConditionLinkAutocomplete
                                        value={selectedConditionLinkIds}
                                        onChange={(conditionLinkIds) => {
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_DYNAMIC_PRICING,
                                                conditionLinkIds,
                                            });
                                        }}
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>

                        <PanelRow>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setRemoveConditions}
                                        onChange={onChangeCheckbox(
                                            'setRemoveConditions'
                                        )}
                                    />
                                }
                                label='Regelkonfiguration zurücksetzen'
                            />
                        </PanelRow>

                        <PanelRow
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setValidityOptions}
                                        onChange={onChangeCheckbox(
                                            'setValidityOptions'
                                        )}
                                    />
                                }
                                label='Verkaufszeitraum für Auswahl festlegen'
                            />

                            {setValidityOptions && (
                                <FlexContainer
                                    sx={{
                                        mx: 3,
                                    }}
                                >
                                    <DateRangePicker
                                        onChange={(nextValue) =>
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_VALIDITY_OPTIONS,
                                                payload: nextValue,
                                            })
                                        }
                                        value={validityRange}
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>

                        <PanelRow
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setRemoveValidFromTo}
                                        onChange={onChangeCheckbox(
                                            'setRemoveValidFromTo'
                                        )}
                                    />
                                }
                                label='Verkaufszeitraum zurücksetzen'
                            />
                        </PanelRow>

                        <PanelRow
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setStartDateValidityOptions}
                                        onChange={onChangeCheckbox(
                                            'setStartDateValidityOptions'
                                        )}
                                    />
                                }
                                label='Gültigkeitszeitraum für Auswahl festlegen'
                            />

                            {setStartDateValidityOptions && (
                                <FlexContainer
                                    sx={{
                                        mx: 3,
                                    }}
                                >
                                    <DateRangePicker
                                        onChange={(nextValue) =>
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_START_DATE_VALIDITY_OPTIONS,
                                                payload: nextValue,
                                            })
                                        }
                                        value={startDateValidityRange}
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>

                        <PanelRow
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setRemoveStartDateValidFromTo}
                                        onChange={onChangeCheckbox(
                                            'setRemoveStartDateValidFromTo'
                                        )}
                                    />
                                }
                                label='Gültigkeitszeitraum zurücksetzen'
                            />
                        </PanelRow>

                        <PanelRow
                            sx={{
                                flexDirection: 'column',
                                alignItems: 'flex-start',
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={setBookingOptions}
                                        onChange={onChangeCheckbox(
                                            'setBookingOptions'
                                        )}
                                    />
                                }
                                label='Buchungsoptionen für markierte Produkte anpassen'
                            />

                            {setBookingOptions && (
                                <FlexContainer
                                    sx={{
                                        mx: 3,
                                    }}
                                >
                                    <BookingOptions
                                        onChange={(configurationCollection) => {
                                            onConfigurationsChange({
                                                type: BulkUpdateType.SET_BOOKING_OPTIONS,
                                                configurations:
                                                    configurationCollection,
                                            });
                                        }}
                                        configurations={configurationCollection}
                                    />
                                </FlexContainer>
                            )}
                        </PanelRow>
                    </CardContent>
                </Card>

                <CardActions>
                    <LoadingButton
                        sx={{
                            position: 'absolute',
                            right: '24px',
                            bottom: '24px',
                        }}
                        onClick={() => {
                            onSave();
                        }}
                        variant='contained'
                        loading={isLoading}
                    >
                        Auswahl speichern
                    </LoadingButton>
                </CardActions>
            </div>
        </Grow>
    );
};
